import { useLocation } from "react-router-dom";

/**
 * 瓦斯訂購 UI 換皮開關（Skin V2）
 * --------------------------------------------------------------------------
 * V2 = 參考稿風格：置中標題列 + 分頁籤、區塊化白卡（無圖示色塊，只留粗體區塊標題）、
 *      pill chips、底部固定「次要外框鈕 + 主要實心鈕」雙鈕列。
 *
 * V2 為正式版預設（先在練習模式驗過再開給所有客戶）。
 *
 * `?skin=v1` 可臨時切回 V1（原樣式，零行為差異），`?skin=v2` 切回新版；
 * 覆寫值寫進 sessionStorage，跨導頁保留（與 practiceMode 同一套做法）。
 * V1 分支刻意保留為 rollback 手段，不是死程式碼 —— 出問題時不必改版即可用網址參數驗證。
 */
export const GAS_SKIN_KEY = "gasShopSkin";

const readOverride = (): "v1" | "v2" | null => {
  if (typeof window === "undefined") return null;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("skin");
    if (fromUrl === "v1" || fromUrl === "v2") sessionStorage.setItem(GAS_SKIN_KEY, fromUrl);
    const saved = sessionStorage.getItem(GAS_SKIN_KEY);
    return saved === "v1" || saved === "v2" ? saved : null;
  } catch {
    return null; // 私密模式等讀寫失敗 → 當作沒覆寫
  }
};

/** 目前瓦斯訂購流程是否套用 V2 換皮。V2 已是正式版；`?skin=v1` 可臨時切回舊版。 */
export const isGasSkinV2 = (): boolean => {
  const override = readOverride();
  if (override) return override === "v2";
  return true;
};

/** 這些路徑屬於瓦斯訂購流程（PageWrapper 依此決定標題列是否換皮）。 */
export const isGasShopPath = (pathname: string): boolean => pathname.startsWith("/gasShop");

/**
 * 帳號區已換皮的頁面。
 *
 * 用逐支列舉而不是 `startsWith("/account")`：帳號區有近三十支頁面，換皮是一支一支推進的。
 * 一次全開的話，還沒改骨架的頁面會出現「V2 的白卡 + V1 的藍色圓角 Header」——
 * 共用元件（SectionLabel／ListContainer／FnRow）是看這個判斷式決定要畫哪一版的。
 * 每換好一支就把它的路徑加進來。
 */
const ACCOUNT_V2_PATHS: RegExp[] = [
  /^\/account$/,
  /^\/account\/detail\/[^/]+$/, // 瓦斯行帳戶明細
  /^\/account\/detail\/[^/]+\/invite$/, // 邀請成員
  /^\/account\/detail\/[^/]+\/members$/, // 成員管理
  /^\/account\/detail\/[^/]+\/orders$/, // 此帳戶的訂單紀錄
  /^\/account\/detail\/[^/]+\/payment-records$/, // 此帳戶的付款紀錄
  /^\/account\/coupons$/, // 優惠券
  /^\/account\/addAccount$/, // 新增／加入瓦斯帳戶（PageWrapper 底下）
  /^\/account\/payRecord$/, // 付費紀錄列表
  /^\/account\/payRecord\/[^/]+$/, // 付費明細
];

/** 套用 V2 頁面骨架（漸層背景 + 釘住的標題列）的路徑：瓦斯訂購流程、訂單列表／詳情、已換皮的帳號頁。 */
export const isSkinV2Path = (pathname: string): boolean =>
  isGasShopPath(pathname) || pathname.startsWith("/orders") || ACCOUNT_V2_PATHS.some((re) => re.test(pathname));

/**
 * 目前這一頁是否套 V2。
 *
 * 給跨頁共用的元件用（如帳號區的 SectionLabel／ListContainer 被十幾個子頁共用）：
 * 換皮是逐頁推進的，元件不能只看 isGasSkinV2()，否則還沒換的 V1 子頁會被一起改掉。
 */
export const useSkinV2Page = (): boolean => {
  const { pathname } = useLocation();
  return isGasSkinV2() && isSkinV2Path(pathname);
};

/**
 * 淡藍霧（haze）＝ Skin V2 的頁首做法。
 *
 * 頁首不是一條有色的藍帶，而是一團從頂端中央散開的極淡光暈（見 all.scss 的 .gas-skin-v2-bg）。
 * 底太亮，白字完全讀不到，所以標題、副標、身分／地址、摘要、分頁籤一律改深色——
 * 凡是原本寫死 #fff（因為底下有實藍）的頁首元件都要問這個判斷。
 *
 * 目前與 isSkinV2Path 同範圍；保留成獨立函式是因為「這頁套 V2 骨架」和「這頁的頁首是淡底」
 * 是兩件事，之後若有某頁要換回深色頁首，只改這裡而不必動骨架判斷。
 */
export const isHazePath = (pathname: string): boolean => isSkinV2Path(pathname);

/** 目前這一頁的頁首是否為淡霧（給頁首共用元件決定文字要白色還是深藍）。 */
export const useHazePage = (): boolean => {
  const { pathname } = useLocation();
  /* Kiosk 的四頁底色也是淡霧（見 Kiosk/index.tsx 等掛的 .gas-skin-v2-bg），
     所以頁首的共用元件（身分膠囊、賣方身分）在那裡也要走深色。

     刻意不併進 isHazePath 走 isGasSkinV2() 的判斷：?skin=v1 是「瓦斯訂購」的 rollback
     手段，不該連帶把 kiosk 的頁首字色翻回白色——kiosk 沒有 V1 版本可以退。 */
  if (pathname.startsWith("/kiosk")) return true;
  return isGasSkinV2() && isHazePath(pathname);
};
